import React, { useState } from 'react';
import { DiscussionComment, CreateCommentRequest } from '../../types/grammarPractice';
import { addComment, updateComment, deleteComment } from '../../api/grammarPracticeApi';
import AddCommentForm from './AddCommentForm';

interface CommentItemProps {
  comment: DiscussionComment;
  exerciseId: number;
  onCommentUpdated: () => void;
  isReply?: boolean;
}

const CommentItem: React.FC<CommentItemProps> = ({
  comment,
  exerciseId,
  onCommentUpdated,
  isReply = false,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState(comment.content);
  const [editIsIssue, setEditIsIssue] = useState(comment.is_issue_report);
  const [showReplyForm, setShowReplyForm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [saving, setSaving] = useState(false);

  // Get current user info from localStorage (you may want to use a context instead)
  const getCurrentUserId = (): number | null => {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      try {
        const user = JSON.parse(userStr);
        return user.id || null;
      } catch {
        return null;
      }
    }
    return null;
  };

  const currentUserId = getCurrentUserId();
  const isOwnComment = currentUserId === comment.user.id;

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const hours = Math.floor(diff / (1000 * 60 * 60));

    if (hours < 1) {
      const minutes = Math.floor(diff / (1000 * 60));
      return `${minutes}m ago`;
    } else if (hours < 24) {
      return `${hours}h ago`;
    } else {
      const days = Math.floor(hours / 24);
      return `${days}d ago`;
    }
  };

  const handleEdit = async () => {
    if (!editContent.trim()) return;

    try {
      setSaving(true);
      await updateComment(comment.comment_id, {
        content: editContent,
        is_issue_report: editIsIssue,
      });
      setIsEditing(false);
      onCommentUpdated();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to update comment');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this comment?')) {
      return;
    }

    try {
      setDeleting(true);
      await deleteComment(comment.comment_id);
      onCommentUpdated();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to delete comment');
      setDeleting(false);
    }
  };

  const handleReply = async (request: CreateCommentRequest) => {
    try {
      await addComment(exerciseId, {
        ...request,
        parent_comment: comment.comment_id,
      });
      setShowReplyForm(false);
      onCommentUpdated();
    } catch (err) {
      throw err;
    }
  };

  return (
    <div className={`space-y-2 ${isReply ? 'ml-8' : ''}`}>
      <div className="bg-base-100 rounded-lg p-4 space-y-3">
        {/* Comment header */}
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-2">
            <span className="font-semibold">
              {comment.user.first_name || comment.user.email}
            </span>
            {comment.is_staff_comment && (
              <span className="badge badge-warning badge-sm gap-1">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-3 w-3"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                </svg>
                Staff
              </span>
            )}
            {comment.is_issue_report && (
              <span className="badge badge-error badge-sm">Issue Report</span>
            )}
            <span className="text-xs text-base-content/50">
              {formatDate(comment.created_at)}
            </span>
          </div>

          {isOwnComment && !isEditing && (
            <div className="dropdown dropdown-end">
              <label tabIndex={0} className="btn btn-ghost btn-xs">
                •••
              </label>
              <ul
                tabIndex={0}
                className="dropdown-content z-[1] menu p-2 shadow bg-base-200 rounded-box w-32"
              >
                <li>
                  <button onClick={() => setIsEditing(true)}>Edit</button>
                </li>
                <li>
                  <button onClick={handleDelete} disabled={deleting}>
                    {deleting ? 'Deleting...' : 'Delete'}
                  </button>
                </li>
              </ul>
            </div>
          )}
        </div>

        {/* Comment content */}
        {isEditing ? (
          <div className="space-y-2">
            <textarea
              className="textarea textarea-bordered w-full"
              rows={3}
              value={editContent}
              onChange={(e) => setEditContent(e.target.value)}
              disabled={saving}
            />
            <div className="flex items-center gap-2">
              <label className="label cursor-pointer gap-2">
                <input
                  type="checkbox"
                  className="checkbox checkbox-sm"
                  checked={editIsIssue}
                  onChange={(e) => setEditIsIssue(e.target.checked)}
                  disabled={saving}
                />
                <span className="label-text text-sm">Report issue</span>
              </label>
            </div>
            <div className="flex gap-2">
              <button
                className="btn btn-sm btn-primary"
                onClick={handleEdit}
                disabled={saving || !editContent.trim()}
              >
                {saving ? 'Saving...' : 'Save'}
              </button>
              <button
                className="btn btn-sm btn-ghost"
                onClick={() => {
                  setIsEditing(false);
                  setEditContent(comment.content);
                  setEditIsIssue(comment.is_issue_report);
                }}
                disabled={saving}
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <>
            <p className="whitespace-pre-wrap">{comment.content}</p>

            {/* Reply button (only for top-level comments) */}
            {!isReply && (
              <div>
                <button
                  className="btn btn-ghost btn-xs"
                  onClick={() => setShowReplyForm(!showReplyForm)}
                >
                  Reply {comment.reply_count > 0 && `(${comment.reply_count})`}
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Reply form */}
      {showReplyForm && !isReply && (
        <div className="ml-8">
          <AddCommentForm
            onSubmit={handleReply}
            onCancel={() => setShowReplyForm(false)}
            placeholder="Write a reply..."
            submitButtonText="Reply"
          />
        </div>
      )}

      {/* Replies */}
      {!isReply && comment.replies && comment.replies.length > 0 && (
        <div className="space-y-2">
          {comment.replies.map((reply) => (
            <CommentItem
              key={reply.comment_id}
              comment={reply}
              exerciseId={exerciseId}
              onCommentUpdated={onCommentUpdated}
              isReply={true}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default CommentItem;
